import * as React from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ui.css';
import { token, zVar } from './theme';

/*
 * Toast — transient messages, mostly "that action failed and here's why". The
 * server's error bodies used to be console.log'd and never seen; anything that
 * can fail mid-game should call useToast().error(...) instead.
 *
 * Wrap the app once in <ToastProvider>; components call useToast().
 */

type ToastKind = 'error' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  error: (message: string) => void;
  info: (message: string) => void;
}

const VISIBLE_MS = 4000;

const ToastContext = createContext<ToastApi | undefined>(undefined);

export const ToastProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, kind, message }]);
    window.setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), VISIBLE_MS);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="ui-toasts" role="status" aria-live="polite">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className="ui-toast"
              style={{
                borderColor: token(toast.kind === 'error' ? 'color-lose' : 'color-border'),
                zIndex: zVar('z-modal'),
              }}
            >
              {toast.message}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside a ToastProvider');
  return api;
};
